import BrandCard from '@/components/BrandCard';
import GridBrandCard from '@/components/layout/GridBrandCard';
import Loader from '@/components/Loader';
import BaseLayout from '@/layout/BaseLayout';
import { trpc } from '@/trpcClient';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import { EBrandCategory } from '@shelflix/schemas/brand/enums';
import type { ListBrandFilterTypeWithValues } from '@shelflix/schemas/brand/list';
import { BrandDto } from '@shelflix/trpc/routes/brand/list/useCase';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MediaBrandFilter } from './MediaBrandFilter';

export function MediaBrandList() {
  const [filters, setFilters] = useState<ListBrandFilterTypeWithValues>({
    sort: { field: 'name', direction: 'asc' },
  });
  const location = useLocation();
  const hasShownToast = useRef(false);
  const navigate = useNavigate();

  const { isMobile } = useDeviceDetect();

  const [page] = useState(1);
  const pageSize = 100;

  const { data, isLoading, isError } = useQuery(
    trpc.brand.listForMedia.queryOptions({
      page,
      recordsPerPage: pageSize,
      ...(filters.search?.value && { search: { ...filters.search } }),
      ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
      ...(filters.sort && { sort: filters.sort }),
    })
  );

  const brands = data?.brands ?? [];

  useEffect(() => {
    if (location.state?.toastSuccess && !hasShownToast.current) {
      toast.success('Alteração salva com sucesso');
      hasShownToast.current = true;

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // const { data: summary = null } = useQuery(trpc.media.getMediaSummary.queryOptions({}));

  // const mediaSummaryData: SummaryItem[] = [
  //   {
  //     title: 'Ativas',
  //     count: summary?.activeMediaTotal ?? 0,
  //     icon: { name: 'check_box', fill: 1 },
  //     color: '#28A745',
  //   },
  //   {
  //     title: 'Marcas',
  //     count: summary?.activeBrandTotal ?? 0,
  //     icon: { name: 'label', fill: 1 },
  //     color: '#005FCC',
  //   },
  // ];

  const groupedBrands = useMemo(
    () =>
      brands.reduce(
        (acc, brand) => {
          if (!acc[brand.category as EBrandCategory]) {
            acc[brand.category] = [];
          }
          acc[brand.category].push(brand);
          return acc;
        },
        {} as Record<string, BrandDto[]>
      ),
    [brands]
  );

  return (
    <BaseLayout>
      <section className="flex flex-col">
        {/* <Summary summaryData={mediaSummaryData} title="Resumo de mídias" /> */}
        <MediaBrandFilter onChange={setFilters} />
        <section
          className="flex w-full flex-wrap overflow-hidden rounded-[5px]"
          style={{ gap: isMobile ? '8px' : '28px' }}
        >
          {isLoading && <Loader message="Carregando marcas..." />}

          {isError && <p className="p-4 text-red-500">Erro ao carregar marcas.</p>}

          {!isLoading &&
            !isError &&
            !!brands.length &&
            Object.entries(groupedBrands)
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([category, brands]) => (
                <Fragment key={category}>
                  <h2 className="col-span-full mt-6 flex items-center gap-2 text-xl font-semibold">
                    <div className="h-6 w-2 rounded-full bg-[var(--primary)]"></div>
                    {category === EBrandCategory.MULTIBRAND ? 'Multimarcas' : 'Exclusivas'}
                    <span className="text-sm font-normal text-gray-500">
                      ({brands.length} materi{brands.length > 1 ? 'ais' : 'al'})
                    </span>
                  </h2>
                  <GridBrandCard>
                    {brands.map((brand) => (
                      <BrandCard className="w-[48.5%] md:w-[242px]" key={brand.uuid} {...brand} />
                    ))}
                  </GridBrandCard>
                </Fragment>
              ))}
          {!isLoading && !isError && !brands.length && <p className="p-4">Nenhuma marca cadastrada</p>}
        </section>
      </section>
    </BaseLayout>
  );
}
